import React, { createContext, useContext, useState, useCallback } from "react";

const STORAGE_KEY = "evg_cookie_preferences_v1";

const defaultPrefs = {
  necessary: true,
  analytics: false,
  marketing: false,
  consentGiven: false,
  decidedAt: null,
};

function readStoredPrefs() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

const CookieContext = createContext({
  prefs: defaultPrefs,
  showBanner: false,
  showPreferences: false,
  openPreferences: () => {},
  closePreferences: () => {},
  acceptAll: () => {},
  rejectNonEssential: () => {},
  savePreferences: () => {},
});

export function CookieProvider({ children }) {
  const initial = readStoredPrefs();
  const [prefs, setPrefs] = useState(initial || defaultPrefs);
  const [showBanner, setShowBanner] = useState(() => !initial || !initial.consentGiven);
  const [showPreferences, setShowPreferences] = useState(false);

  const persist = useCallback((next) => {
    const decided = { ...next, consentGiven: true, decidedAt: new Date().toISOString() };
    setPrefs(decided);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(decided));
    } catch {
      /* ignore quota errors */
    }
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  const acceptAll = useCallback(() => {
    persist({ necessary: true, analytics: true, marketing: true });
  }, [persist]);

  const rejectNonEssential = useCallback(() => {
    persist({ necessary: true, analytics: false, marketing: false });
  }, [persist]);

  const savePreferences = useCallback((next) => {
    persist({ necessary: true, analytics: !!next.analytics, marketing: !!next.marketing });
  }, [persist]);

  const openPreferences = useCallback(() => setShowPreferences(true), []);
  const closePreferences = useCallback(() => setShowPreferences(false), []);

  return (
    <CookieContext.Provider
      value={{
        prefs,
        showBanner,
        showPreferences,
        openPreferences,
        closePreferences,
        acceptAll,
        rejectNonEssential,
        savePreferences,
      }}
    >
      {children}
    </CookieContext.Provider>
  );
}

export const useCookieConsent = () => useContext(CookieContext);
